// src/components/ResultModal.js
import React, { useState, useEffect } from 'react';
import { 
    Download, Trash2, X, Star, FolderPlus, ArrowLeft, Check, Loader2, Save, Folder, Lock 
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import { BACKEND_URL, supabase } from '../supabaseClient'; 
import './ResultModal.css';

const ResultModal = ({ isOpen, onClose, imageUrl, mockupId, session }) => {
    // --- 1. HOOKS ---
    const [view, setView] = useState('preview'); 
    const [isDeleting, setIsDeleting] = useState(false);
    const [isFavorite, setIsFavorite] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    
    // Tier State
    const [userTier, setUserTier] = useState('starter'); 

    // Naming State
    const [mockupName, setMockupName] = useState('');
    
    // Folder State
    const [folders, setFolders] = useState([]);
    const [isLoadingFolders, setIsLoadingFolders] = useState(false);
    const [selectedFolderId, setSelectedFolderId] = useState(null);

    // --- EFFECT: Initialize & Check Tier ---
    useEffect(() => {
        if (isOpen) {
            // 1. Set Default Name
            const uniqueId = Math.floor(1000 + Math.random() * 9000); 
            setMockupName(`FullMockup-Gen-${uniqueId}`);
            
            // 2. Reset View
            setView('preview');
            setIsFavorite(false);

            // 3. Check User Tier
            const checkTier = async () => {
                if (!session?.user?.id) return;
                const { data, error } = await supabase
                    .from('profiles')
                    .select('subscription_tier')
                    .eq('id', session.user.id)
                    .single();
                
                if (data && !error) {
                    const tier = (data.subscription_tier || 'starter').toLowerCase();
                    setUserTier(tier);
                    console.log("Current User Tier:", tier); // 🟢 Debug Log
                }
            };
            checkTier();
        }
    }, [isOpen, session]);

    useEffect(() => {
        if (isOpen && view === 'folders' && session?.access_token) {
            if (userTier === 'starter') return; 

            const fetchFolders = async () => {
                setIsLoadingFolders(true);
                try {
                    const response = await fetch(`${BACKEND_URL}/api/gallery/folders`, {
                        headers: { 'Authorization': `Bearer ${session.access_token}` }
                    });
                    if (response.ok) setFolders(await response.json());
                } catch (err) { toast.error("Could not load folders"); } 
                finally { setIsLoadingFolders(false); }
            };
            fetchFolders();
        }
    }, [view, session, isOpen, userTier]);

    // --- HELPER: Upsell Trigger (The Notification Popup) ---
    const triggerUpsell = (featureName, message) => {
        console.log(`🔒 Triggering Upsell for: ${featureName}`); // 🟢 Debug Log
        
        toast((t) => (
            <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: '16px', 
                minWidth: '300px',
                fontFamily: 'Inter, sans-serif'
            }}>
                <div style={{ 
                    background: '#FEF2F2', 
                    padding: '10px', 
                    borderRadius: '10px', 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    border: '1px solid #FECACA'
                }}>
                    <Lock size={20} color="#DC2626" />
                </div>
                <div style={{ flex: 1 }}>
                    <b style={{ fontSize: '14px', display:'block', color: '#1F2937', marginBottom: '2px' }}>
                        {featureName} Locked
                    </b>
                    <span style={{ fontSize: '13px', color: '#6B7280', lineHeight: '1.4' }}>
                        {message || "Upgrade to Pro to save & organize."}
                    </span>
                </div>
                <button 
                    onClick={() => { window.location.href = '/pricing'; toast.dismiss(t.id); }}
                    style={{ 
                        background: '#2563EB', 
                        color: 'white', 
                        border: 'none', 
                        padding: '8px 16px', 
                        borderRadius: '8px', 
                        fontSize: '13px', 
                        cursor: 'pointer', 
                        fontWeight: '600',
                        transition: 'background 0.2s',
                        boxShadow: '0 2px 4px rgba(37, 99, 235, 0.2)'
                    }}
                >
                    Upgrade
                </button>
            </div>
        ), { 
            id: 'upsell-toast', // 🟢 Prevents duplicate toasts
            duration: 1500, 
            position: 'bottom-center',
            style: {
                borderRadius: '12px',
                background: '#fff',
                boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                padding: '12px',
                border: '1px solid #E5E7EB',
                maxWidth: '400px',
                zIndex: 99999 // Force style level z-index as backup
            }
        });
    };

    // --- 2. ACTIONS ---

    // A. DOWNLOAD 
    const handleDownload = async () => {
        setIsDownloading(true);
        try {
            const response = await fetch(imageUrl, { mode: 'cors', cache: 'no-cache' });
            if (!response.ok) throw new Error("Fetch failed");
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            
            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = `${mockupName.trim() || 'mockup'}.png`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            
            window.URL.revokeObjectURL(blobUrl);
            toast.success("Download started");
        } catch (err) {
            console.error(err);
            window.open(imageUrl, '_blank');
        } finally { setIsDownloading(false); }
    };

    // B. SAVE (Restricted)
    const handleQuickSave = async () => {
        if (userTier === 'starter') return triggerUpsell("Cloud Gallery", "Saving to the cloud is a paid feature.");
        
        if (!mockupId) return toast.error("Error: Missing ID");
        
        setIsSaving(true);
        try {
            await fetch(`${BACKEND_URL}/api/gallery/mockups/${mockupId}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ name: mockupName })
            });
            toast.success("Saved to Gallery");
            onClose(); 
        } catch (err) {
            toast.error("Save failed");
        } finally {
            setIsSaving(false);
        }
    };

    // C. SAVE TO FOLDER (Restricted)
    const handleSwitchToFolders = () => {
        if (userTier === 'starter') return triggerUpsell("Folders", "Organizing into folders is for Pro users.");
        setView('folders');
    };

    // D. SAVE TO FOLDER CONFIRM
    const handleConfirmSaveToFolder = async () => {
        if (!selectedFolderId) { toast.error("Select a folder"); return; }
        if (!mockupId) return toast.error("Error: Missing ID");

        setIsSaving(true);
        const folderIdToSend = selectedFolderId === 'all' ? null : selectedFolderId;

        try {
            await fetch(`${BACKEND_URL}/api/gallery/mockups/${mockupId}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    name: mockupName,
                    folder_id: folderIdToSend 
                })
            });
            toast.success("Saved to folder");
            onClose();
        } catch (err) { toast.error("Save failed"); } 
        finally { setIsSaving(false); }
    };

    // E. FAVORITE (Restricted)
    const toggleFavorite = async () => {
        if (userTier === 'starter') return triggerUpsell("Favorites", "Marking favorites is a paid feature.");
        
        if (!mockupId) return;
        
        const newState = !isFavorite;
        setIsFavorite(newState); 
        
        try {
            await fetch(`${BACKEND_URL}/api/gallery/mockups/${mockupId}`, {
                method: 'PUT',
                headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ is_favorite: newState })
            });
        } catch (err) { 
            setIsFavorite(!newState); 
            toast.error("Failed to favorite");
        }
    };

    // F. DISCARD
    const handleDiscard = async () => {
        if (!mockupId) return;
        const confirmMsg = userTier === 'starter' 
            ? "Discard this result?" 
            : "Delete this image from your gallery?";

        if (!window.confirm(confirmMsg)) return;
        
        setIsDeleting(true);
        try {
            await fetch(`${BACKEND_URL}/api/gallery/mockups/${mockupId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${session.access_token}` }
            });
            onClose();
        } catch (e) { setIsDeleting(false); }
    };

    // Folder Creation
    const handleCreateFolder = async () => {
        const name = prompt("Folder Name:");
        if (!name) return;
        try {
            const res = await fetch(`${BACKEND_URL}/api/gallery/folders`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({ name })
            });
            if (res.ok) setFolders([...folders, await res.json()]);
        } catch (e) { toast.error("Error creating folder"); }
    };

    if (!isOpen) return null;

    return (
        <div className="modal-overlay">
            <div className="modal-content">
                
                {/* --- HEADER --- */}
                <div className="modal-header">
                    <div className="header-left">
                        {view === 'folders' && <button onClick={() => setView('preview')} className="back-btn"><ArrowLeft size={18}/></button>}
                        <h3>{view === 'folders' ? 'Select Folder' : 'Result Ready'}</h3>
                    </div>
                    <div className="header-actions">
                        <button onClick={onClose} className="close-btn"><X size={20}/></button>
                    </div>
                </div>

                {/* --- BODY --- */}
                <div className="modal-body">
                    {view === 'preview' ? (
                        <div className="preview-layout">
                            {/* Image */}
                            <div className="image-container result-img">
                                <img src={imageUrl} alt="Generated Result" />
                                
                                <button 
                                    onClick={toggleFavorite} 
                                    className={`fav-float ${isFavorite ? 'active' : ''}`} 
                                    title="Favorite"
                                    style={userTier === 'starter' ? { opacity: 0.9, background: 'white' } : {}}
                                >
                                    {userTier === 'starter' ? (
                                        <Lock size={16} color="#E53E3E" />
                                    ) : (
                                        <Star size={20} fill={isFavorite ? "gold" : "none"} color={isFavorite ? "gold" : "#6B7280"} />
                                    )}
                                </button>
                            </div>

                            {/* Name Input Section */}
                            <div className="naming-section">
                                <label>File Name</label>
                                <input 
                                    type="text" 
                                    value={mockupName} 
                                    onChange={(e) => setMockupName(e.target.value)} 
                                    className="main-name-input"
                                />
                            </div>
                        </div>
                    ) : (
                        <div className="folder-explorer">
                            {isLoadingFolders ? <div className="loading-state"><Loader2 className="animate-spin" /></div> : (
                                <div className="folder-list">
                                    <div className={`folder-item ${selectedFolderId === 'all' ? 'selected' : ''}`} onClick={() => setSelectedFolderId('all')}>
                                        <Folder size={20} className="icon"/> <span>All Images</span>
                                        {selectedFolderId === 'all' && <Check size={16}/>}
                                    </div>
                                    {folders.map(f => (
                                        <div key={f.id} className={`folder-item ${selectedFolderId === f.id ? 'selected' : ''}`} onClick={() => setSelectedFolderId(f.id)}>
                                            <Folder size={20} className="icon"/> <span>{f.name}</span>
                                            {selectedFolderId === f.id && <Check size={16}/>}
                                        </div>
                                    ))}
                                </div>
                            )}
                            <button onClick={handleCreateFolder} className="new-folder-btn"><FolderPlus size={16}/> New Folder</button>
                        </div>
                    )}
                </div>

                {/* --- FOOTER --- */}
                <div className="modal-footer">
                    {view === 'preview' ? (
                        <>
                            <div className="footer-left">
                                <button onClick={handleDownload} disabled={isDownloading} className="btn-secondary">
                                    {isDownloading ? <Loader2 size={16} className="spin"/> : <Download size={16}/>} Download
                                </button>
                                <button onClick={handleDiscard} className="btn-text-danger">Discard</button>
                            </div>
                            
                            <div className="footer-right">
                                <button onClick={handleSwitchToFolders} className="btn-secondary" title="Save to Folder">
                                    <Folder size={16}/> 
                                    <span style={{marginLeft: '6px'}}>Save to...</span>
                                    {userTier === 'starter' && <Lock size={14} style={{marginLeft: 6, color: '#DC2626'}}/>}
                                </button>
                                <button onClick={handleQuickSave} disabled={isSaving} className="btn-primary">
                                    {isSaving ? 'Saving...' : <><Save size={16}/> Save</>}
                                    {userTier === 'starter' && <Lock size={14} style={{marginLeft: 6, opacity: 0.8}}/>}
                                </button>
                            </div>
                        </>
                    ) : (
                        <>
                            <button onClick={() => setView('preview')} className="btn-secondary">Back</button>
                            <button onClick={handleConfirmSaveToFolder} disabled={isSaving} className="btn-primary">
                                {isSaving ? 'Saving...' : 'Confirm Save'}
                            </button>
                        </>
                    )}
                </div>

            </div>
        </div>
    );
};

export default ResultModal;